import React from "react";
import { Stack } from "@fluentui/react";
import { DismissRegular } from "@fluentui/react-icons";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import type { ConsoleCitation } from "../../api/types";
import { normalizeCitationMarkdownPreview } from "../../utils/citationMarkdownPreview";
import styles from "./CitationPanel.module.css";

/**
 * Product Chat.tsx onShowCitation tuple, copied so the console panel reads the
 * same slots Cordexa does: content, id, title, filepath, unused, unused, document_id.
 */
export type ActiveCitationTuple = [
  string,
  string | undefined,
  string,
  string,
  string,
  string,
  string | null,
];

export function activeCitationFromProduct(
  citation: ConsoleCitation,
): ActiveCitationTuple {
  return [
    citation.content || "",
    citation.id,
    citation.title ?? "",
    citation.filepath ?? "",
    "",
    "",
    citation.document_id ?? null,
  ];
}

type CitationPanelProps = {
  activeCitation: ActiveCitationTuple | null;
  setIsCitationPanelOpen: (flag: boolean) => void;
  isOpen?: boolean;
};

export const CitationPanel: React.FC<CitationPanelProps> = (props) => {
  const { activeCitation, setIsCitationPanelOpen, isOpen = false } = props;

  if (!activeCitation) {
    return (
      <Stack.Item
        className={`${styles.citationPanel} ${isOpen ? styles.open : ""}`}
        data-testid="citation-panel"
      >
        <Stack
          horizontal
          className={styles.citationPanelHeaderContainer}
          horizontalAlign="space-between"
          verticalAlign="center"
        >
          <span className={styles.citationPanelHeader}>Citations</span>
          <DismissRegular
            role="button"
            aria-label="Close citations"
            onKeyDown={(event) =>
              event.key === " " || event.key === "Enter"
                ? setIsCitationPanelOpen(false)
                : null
            }
            tabIndex={0}
            className={styles.citationPanelDismiss}
            onClick={() => setIsCitationPanelOpen(false)}
          />
        </Stack>
      </Stack.Item>
    );
  }

  const documentTitle = (activeCitation[2] as string) || "document";
  const citationMarkdown = normalizeCitationMarkdownPreview(
    String(activeCitation[0] ?? ""),
  );

  return (
    <Stack.Item
      className={`${styles.citationPanel} ${isOpen ? styles.open : ""}`}
      data-testid="citation-panel"
    >
      <Stack
        horizontal
        className={styles.citationPanelHeaderContainer}
        horizontalAlign="space-between"
        verticalAlign="center"
      >
        <span className={styles.citationPanelHeader}>Citations</span>
        <DismissRegular
          role="button"
          aria-label="Close citations"
          onKeyDown={(event) =>
            event.key === " " || event.key === "Enter"
              ? setIsCitationPanelOpen(false)
              : null
          }
          tabIndex={0}
          className={styles.citationPanelDismiss}
          onClick={() => setIsCitationPanelOpen(false)}
        />
      </Stack>
      <h5
        className={styles.citationPanelTitle}
        data-testid="citation-panel-title"
      >
        {documentTitle}
      </h5>
      <div className={styles.citationPanelDisclaimer}>
        Tables, images, and other special formatting not shown in this preview.
        Please follow the link to review the original document.
      </div>
      <ReactMarkdown
        className={styles.citationPanelContent}
        remarkPlugins={[remarkGfm]}
      >
        {citationMarkdown}
      </ReactMarkdown>
    </Stack.Item>
  );
};
